import createCachedSelector from "re-reselect";
import { ApplicationStore } from "src/store";
import {
    getPaymentFrequency,
    getSelectedOffer,
    getSelectedPayment,
    getSelectedPaymentType,
    getTotalMonthlyTaxes,
    getVehicle,
} from "src/store/selectors/vehicles";
import { localizeCurrencyToWholeNumber, localizeCurrencyWithDecimals } from "src/utils";
import { Accessory } from "Types/api/Accessory";
import { LeasePayment } from "Types/api/LeasePayment";
import { LoanPayment } from "Types/api/LoanPayment";
import { OfferFrequency } from "Types/api/OfferFrequency";

export const isAccessoriesEnabled = (state: ApplicationStore) => {
    return state.account.accessories_enabled;
};

export const getAccessoryImage = (state: ApplicationStore, vin: string, accessoryId: number) => {
    return state.accessories?.[vin]?.entities?.[accessoryId]?.thumbnail_url ?? null;
};

export const getAccessoryTitle = (state: ApplicationStore, vin: string, accessoryId: number) => {
    return state.accessories?.[vin]?.entities?.[accessoryId]?.title ?? "";
};

export const getAccessoryDocumentTitle = (state: ApplicationStore, vin: string, accessoryId: number) => {
    return state.accessories?.[vin]?.entities?.[accessoryId]?.document_title;
};

export const getAccessoryDocumentUrl = (state: ApplicationStore, vin: string, accessoryId: number) => {
    return state.accessories?.[vin]?.entities?.[accessoryId]?.document_url || undefined;
};

export const getAccessoryDescription = (state: ApplicationStore, vin: string, accessoryId: number) => {
    return state.accessories?.[vin]?.entities?.[accessoryId]?.description ?? "";
};

export const getAccessoryExcerpt = (state: ApplicationStore, vin: string, accessoryId: number) => {
    return state.accessories?.[vin]?.entities?.[accessoryId]?.excerpt ?? "";
};

export const getAccessoryPrice = (state: ApplicationStore, vin: string, accessoryId: number) => {
    const price = state.accessories?.[vin]?.entities?.[accessoryId]?.price;

    if (price !== undefined && price > 0) {
        return price;
    }

    return undefined;
};

export const getAccessoryPriceDisplayText = (state: ApplicationStore, vin: string, accessoryId: number) => {
    const price = getAccessoryPrice(state, vin, accessoryId);

    if (price !== undefined) {
        return localizeCurrencyToWholeNumber(price);
    }

    return undefined;
};

export const getAccessoryPriceAltText = (state: ApplicationStore, vin: string, accessoryId: number) => {
    const price = state.accessories?.[vin]?.entities?.[accessoryId]?.price;
    const priceAltText = state.accessories?.[vin]?.entities?.[accessoryId]?.price_text;

    if ((price === undefined || price === 0) && priceAltText !== undefined) {
        return priceAltText;
    }

    return undefined;
};

export const getAccessoryPaymentDisplayText = createCachedSelector(
    getAccessoryPrice,
    getSelectedPaymentType,
    getSelectedPayment,
    getSelectedOffer,
    getTotalMonthlyTaxes,
    (price, paymentType, payment, offer, taxes) => {
        if (price === undefined || offer === null) {
            return undefined;
        }

        switch (paymentType) {
            case "lease":
                const leasePayment =
                    accessoryLeasePayment(
                        offer.amount_financed + price,
                        (offer as Exclude<LeasePayment["offers"], never | undefined>[number]).residual,
                        (offer as Exclude<LeasePayment["offers"], never | undefined>[number]).money_factor,
                        (payment as LeasePayment).term!,
                        (offer as Exclude<LeasePayment["offers"], never | undefined>[number]).frequency
                    ) -
                    (offer as Exclude<LeasePayment["offers"], never | undefined>[number]).payment +
                    taxes;
                return localizeCurrencyWithDecimals(leasePayment);
            case "loan":
                const loanPayment = accessoryLoanPayment(
                    price,
                    (offer as Exclude<LoanPayment["offers"], never | undefined>[number]).interest_rate,
                    (payment as LeasePayment).term!,
                    (offer as Exclude<LoanPayment["offers"], never | undefined>[number]).frequency
                );
                return localizeCurrencyWithDecimals(loanPayment);
            default:
                return undefined;
        }
    }
)((state, vin, accessoryId) => `${vin}:${getSelectedPaymentType(state, vin)}:${accessoryId}`);

const accessoryLeasePayment = (
    total: number,
    residual: number,
    moneyFactor: number,
    term: number,
    frequency: OfferFrequency
) => {
    const depreciation = +total - +residual;
    const rent = (+total + +residual) * +moneyFactor * +term;

    const monthlyPayment = (depreciation + rent) / term;
    if (frequency === "biweekly") {
        return monthlyPayment * (12 / 26);
    }

    if (frequency === "weekly") {
        return monthlyPayment * (12 / 52);
    }

    return monthlyPayment;
};

const accessoryLoanPayment = (price: number, apr: number, term: number, frequency: OfferFrequency) => {
    const periodRate = apr / 12;
    const monthlyPayment =
        Number(apr) === 0 ? price / term : (price * periodRate) / (1 - Math.pow(1 + periodRate, -term));

    if (frequency === "biweekly") {
        return monthlyPayment * (12 / 26);
    }

    if (frequency === "weekly") {
        return monthlyPayment * (12 / 52);
    }

    return monthlyPayment;
};

export const getAccessoryPaymentFrequencyDisplayKey = (state: ApplicationStore, vin: string) => {
    const paymentFrequency = getPaymentFrequency(state, vin);

    switch (paymentFrequency) {
        case "monthly":
            return "CalculatedPayment.paymentFrequency.month";
        case "biweekly":
            return "CalculatedPayment.paymentFrequency.bi-weekly";
        case "weekly":
            return "CalculatedPayment.paymentFrequency.weekly";
        default:
            return undefined;
    }
};

export const getAccessoriesTotal = createCachedSelector(
    getVehicle,
    (vehicle) =>
        vehicle?.accessories?.reduce((total: number, accessory: Accessory) => total + (accessory.price ?? 0), 0) ?? 0
)((state, vin) => vin);

export const getSelectedAccessories = createCachedSelector(getVehicle, (vehicle) => {
    return vehicle?.accessories ?? [];
})((state, vin) => vin);
